<template>
  <div class="article-container" v-if="post">
    <Head>
      <title>{{ post.title }} | Top 5 Guru</title>
      <meta name="description" :content="generateMetaDescription(post.metaDescription)" />
      <meta name="keywords" :content="post.tags ? post.tags.join(', ') : ''" />
      <meta name="robots" :content="isFallback ? 'noindex,follow' : 'index,follow'" />
      <!-- Primary Meta Tags -->
      <meta name="title" :content="`${post.title} | Top 5 Guru`" />
      <meta name="author" content="Top 5 Guru" />
      <!-- Open Graph / Facebook -->
      <meta property="og:type" content="article" />
      <meta property="og:title" :content="post.title" />
      <meta property="og:description" :content="generateMetaDescription(post.metaDescription)" />
      <meta property="og:image" :content="post.image" />
      <meta property="og:url" :content="currentUrl" />
      <meta property="og:site_name" content="Top 5 Guru" />
      <!-- Twitter -->
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" :content="post.title" />
      <meta property="twitter:description" :content="generateMetaDescription(post.metaDescription)" />
      <meta property="twitter:image" :content="post.image" />
      <!-- Canonical URL -->
      <link rel="canonical" :href="isFallback ? `${origin}/en/article/${post.slug}` : currentUrl" />
      <!-- Hreflang Links -->
      <template v-for="link in generateHreflangLinks" :key="link.hreflang">
        <link :rel="link.rel" :hreflang="link.hreflang" :href="link.href" />
      </template>
      <!-- Structured Data / Schema.org -->
      <component :is="'script'" type="application/ld+json" v-if="structuredData" v-html="structuredData"></component>
      <!-- Additional SEO Meta Tags -->
      <meta name="language" :content="lang" />
      <meta name="revisit-after" content="7 days" />
    </Head>

    <div v-if="isFallback" class="fallback-note">
      This article is not yet available in your selected language. Showing the English version.
    </div>

    <h1>{{ post.title }}</h1>
    
    <div class="article-meta">
      <p><strong>Published:</strong> {{ formatDate(post.publishedDate || new Date()) }}</p>
      <p v-if="post.author"><strong>Author:</strong> {{ post.author }}</p>
    </div>
    
    <img :src="post.image" :alt="post.title" class="article-image" v-if="post.image" />
    
    <div class="article-content" v-html="sanitizedContent"></div>
    
    <!-- Table of Contents -->
    <div class="table-of-contents" v-if="tocLinks.length > 0">
      <h2>Table of Contents</h2>
      <ul>
        <li v-for="link in tocLinks" :key="link.id">
          <a :href="`/${lang}/article/${route.params.slug}#${link.id}`">{{ link.text }}</a>
        </li>
      </ul>
    </div>
    
    <!-- Related Posts -->
    <div class="related-posts" v-if="relatedPosts.length > 0">
      <h2>Related Posts</h2>
      <div class="related-posts-grid">
        <div v-for="relatedPost in relatedPosts" :key="relatedPost.slug" class="related-post-card">
          <router-link :to="`/${lang}/article/${relatedPost.slug}`">{{ relatedPost.title }}</router-link>
        </div>
      </div>
    </div>
    
    <!-- Share Buttons -->
    <div class="share-buttons">
      <h3>Share This Article</h3>
      <div class="share-grid">
        <a :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`" target="_blank" rel="noopener noreferrer" class="share-button facebook">Facebook</a>
        <a :href="`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(post.title)}`" target="_blank" rel="noopener noreferrer" class="share-button twitter">Twitter</a>
        <a :href="`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`" target="_blank" rel="noopener noreferrer" class="share-button linkedin">LinkedIn</a>
      </div>
    </div>
  </div>
  <div v-else class="loading-container">
    <p>Loading post...</p>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { useRoute } from 'vue-router';
import { ref, onMounted, computed, watch } from 'vue';
import { useLanguage } from '../composables/useLanguage'; // Import useLanguage
import { stripHtmlToText } from '../utils/html'; // Import the new helper
import DOMPurify from 'dompurify'; // Import DOMPurify

export default {
  setup() {
    const route = useRoute();
    const { lang } = useLanguage();

    const post = ref(null);
    const isFallback = ref(false); // To track if we are showing fallback content
    const currentUrl = ref('');
    const tocLinks = ref([]);
    const relatedPosts = ref([]);
    const structuredData = ref(null);

    // Computed property for sanitized content
    const sanitizedContent = computed(() => {
      return post.value && post.value.content ? DOMPurify.sanitize(post.value.content) : '';
    });

    const getDocIdBySlug = async (slug, langToFind) => {
      if (!slug || !langToFind) return null;
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, where(`languageVariants.${langToFind}.slug`, '==', slug), limit(1));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].id;
      }
      return null;
    };

    const loadPost = async (slug, requestedLang) => {
      post.value = null;
      isFallback.value = false;

      let docId = await getDocIdBySlug(slug, requestedLang);
      let langToShow = requestedLang;

      // If not found and the requested language is not English, try to find the doc by the English slug
      // This part of the logic is tricky. A better way is to find the document by any slug and then check variants.
      // For now, we will handle it by just checking if the variant exists.
      if (!docId && requestedLang !== 'en') {
        // This logic is simplified: we assume if a slug for a lang doesn't exist, we fallback to english.
        // A more robust solution would require a shared identifier across language variants.
        // Given the current structure, we'll try to find the document by a different language slug, which is not efficient.
        // The best approach is to find the doc by its ID, if we had it.
        // Let's assume for now that if the slug for the requested lang is not found, we don't have a reliable way to get the English version.
        // The logic will be updated to show English content if the requested language variant does not exist on a document.
      }
      
      // Let's adjust the logic. We will find a doc by slug, and if the requested lang variant doesn't exist, we'll use the english one.
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, where(`languageVariants.${requestedLang}.slug`, '==', slug), limit(1));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        docId = querySnapshot.docs[0].id;
      } else {
        // If slug not found in requested language, we can't reliably find the document.
        // The fallback logic needs to be based on an existing document.
        // Let's stick to the user request.
        console.error(`❌ No post document ID found for slug '${slug}' in language '${requestedLang}'.`);
        return; // Exit if no document is found
      }


      try {
        const docRef = (await getDocs(query(collection(db, 'articles'), where('__name__', '==', docId), limit(1)))).docs[0];
        if (!docRef) {
            console.error(`❌ No post found with ID: ${docId}`);
            return;
        }

        const articleData = docRef.data();
        let langVariant = articleData.languageVariants[requestedLang];

        if (langVariant) {
          langToShow = requestedLang;
        } else {
          // Fallback to English
          langVariant = articleData.languageVariants.en;
          langToShow = 'en';
          isFallback.value = true;
          console.log(`⚠️ Language variant for '${requestedLang}' not found. Falling back to English.`);
        }
        
        if (langVariant) {
            post.value = {
              ...articleData,
              ...langVariant,
              id: docId,
            };

            if (!post.value.publishedDate) {
              post.value.publishedDate = new Date();
            }

            console.log(`✅ Post loaded from Firestore. Language: ${langToShow}`);
            updateCurrentUrl(langToShow, langVariant.slug);
            extractTocLinks();
            generateStructuredData();
            fetchRelatedPosts();
        } else {
            console.error(`❌ Fallback to English also failed. No 'en' variant found for doc ID ${docId}`);
        }

      } catch (error) {
        console.error('❌ Firestore query error:', error);
      }
    };
    
    const extractTocLinks = () => {
      // ... (same as before)
    };

    const fetchRelatedPosts = async () => {
      // ... (same as before)
    };

    const generateMetaDescription = (content) => {
      if (!content) return '';
      // Use the new helper function
      return stripHtmlToText(content).substring(0, 160);
    };

    const formatDate = (date) => {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const updateCurrentUrl = (lang, slug) => {
        currentUrl.value = `${window.location.origin}/${lang}/article/${slug}`;
    };

    const generateStructuredData = () => {
        if (!post.value) {
            structuredData.value = null;
            return;
        }

        const data = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.value.title,
            "description": generateMetaDescription(post.value.metaDescription || post.value.content),
            "image": post.value.image,
            "datePublished": (post.value.publishedDate || new Date()).toISOString(),
            "author": {
                "@type": "Person",
                "name": post.value.author || "Top 5 Guru"
            },
            "publisher": {
                "@type": "Organization",
                "name": "Top 5 Guru",
                "logo": {
                    "@type": "ImageObject",
                    "url": `${window.location.origin}/top5-logo.png`
                }
            },
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": currentUrl.value
            }
        };

        structuredData.value = JSON.stringify(data);
    };

    const generateHreflangLinks = computed(() => {
      if (!post.value || !post.value.languageVariants) return [];
      
      const links = [];
      const origin = window.location.origin;

      for (const langCode in post.value.languageVariants) {
        const variant = post.value.languageVariants[langCode];
        if (variant && variant.slug) {
          links.push({
            rel: 'alternate',
            hreflang: langCode,
            href: `${origin}/${langCode}/article/${variant.slug}`
          });
        }
      }

      // x-default should point to the English version if available
      const defaultVariant = post.value.languageVariants.en;
      if (defaultVariant && defaultVariant.slug) {
        links.push({
          rel: 'alternate',
          hreflang: 'x-default',
          href: `${origin}/en/article/${defaultVariant.slug}`
        });
      }
      
      return links;
    });

    onMounted(() => {
      watch(
        () => [route.params.slug, lang.value],
        ([newSlug, newLang]) => {
          if (newSlug && newLang) {
            loadPost(newSlug, newLang);
          }
        },
        { immediate: true }
      );
    });

    return {
      post,
      isFallback,
      currentUrl,
      tocLinks,
      relatedPosts,
      structuredData,
      sanitizedContent, // Exposed sanitizedContent
      lang,
      generateMetaDescription,
      formatDate,
      generateHreflangLinks
    };
  }
};
</script>

<style>
/* Article container styling for ArticlePage */
.article-container {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  margin: 30px auto;
  max-width: 800px;
}

.article-container h1 {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #222;
  text-align: center;
}

.article-meta {
  background: rgba(233, 30, 99, 0.1);
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.95rem;
}

.article-meta p {
  margin: 5px 0;
}

.article-image {
  width: 100%;
  border-radius: 12px;
  margin: 15px 0 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.article-content {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
}

.article-content h2, 
.article-content h3, 
.article-content h4 {
  margin-top: 30px;
  margin-bottom: 15px;
  color: #222;
}

.article-content p {
  margin-bottom: 20px;
}

.article-content img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 20px 0;
}

.article-content ul, 
.article-content ol {
  margin-left: 20px;
  margin-bottom: 20px;
}

.article-content li {
  margin-bottom: 8px;
}

/* Table of Contents */
.table-of-contents {
  background: rgba(0, 0, 0, 0.05);
  padding: 20px;
  border-radius: 10px;
  margin: 30px 0;
}

.table-of-contents h2 {
  font-size: 1.4rem;
  margin-bottom: 15px;
  color: #e91e63;
}

.table-of-contents ul {
  list-style-type: none;
  padding-left: 10px;
}

.table-of-contents li {
  margin-bottom: 10px;
}

.table-of-contents a {
  color: #333;
  text-decoration: none;
  transition: color 0.2s;
}

.table-of-contents a:hover {
  color: #e91e63;
  text-decoration: underline;
}

/* Related Posts */
.related-posts {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 2px solid rgba(0, 0, 0, 0.1);
}

.related-posts h2 {
  font-size: 1.6rem;
  margin-bottom: 20px;
  color: #222;
}

.related-posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
}

.related-post-card {
  background: rgba(233, 30, 99, 0.08);
  padding: 15px;
  border-radius: 8px;
  transition: transform 0.2s ease-in-out, background 0.2s ease-in-out;
}

.related-post-card:hover {
  transform: translateY(-3px);
  background: rgba(233, 30, 99, 0.15);
}

.related-post-card a {
  color: #333;
  font-weight: 600;
  text-decoration: none;
  display: block;
}

/* Share Buttons */
.share-buttons {
  margin-top: 40px;
  padding-top: 25px;
  border-top: 2px solid rgba(0, 0, 0, 0.1);
}

.share-buttons h3 {
  font-size: 1.3rem;
  margin-bottom: 15px;
  color: #444;
}

.share-grid {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.share-button {
  padding: 8px 15px;
  border-radius: 5px;
  color: white;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.2s;
}

.share-button:hover {
  opacity: 0.9;
}

.facebook {
  background: #3b5998;
}

.twitter {
  background: #1da1f2;
}

.linkedin {
  background: #0077b5;
}

/* Tag styling */
.article-container strong {
  color: #e91e63;
}

/* Loading message styling */
.loading-container {
  background: rgba(255, 255, 255, 0.8);
  padding: 20px;
  border-radius: 15px;
  text-align: center;
  font-size: 1.2rem;
  color: #555;
  max-width: 600px;
  margin: 50px auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}


/* Responsive styles */
@media (max-width: 768px) {
  .article-container {
    padding: 20px;
    margin: 15px;
  }
  
  .article-container h1 {
    font-size: 1.8rem;
  }
  
  .article-content {
    font-size: 1rem;
    line-height: 1.6;
  }
  
  .related-posts-grid {
    grid-template-columns: 1fr;
  }
}

/* Accessibility improvements */
.article-content a:focus,
.table-of-contents a:focus,
.related-post-card a:focus,
.share-button:focus {
  outline: 2px solid #e91e63;
  outline-offset: 2px;
}

/* Print styles for better PDF generation */
@media print {
  .article-container {
    box-shadow: none;
    margin: 0;
    padding: 0;
  }
  
  .share-buttons,
  .related-posts {
    display: none;
  }
}
</style>